/**
 * widgets/Form.ts · 通用表单组件
 *
 * 所有 form 页 (quotationCreate / customerCreate / poCreate 等) 复用
 *
 * 设计原则:
 *   - 单一来源 · 替代 v200.x 散乱 form HTML
 *   - 标签+input+错误提示统一布局
 *   - 提交按钮 disabled 防重复提交
 *   - 错误友好提示 (replaceWith 不弹 alert)
 */

export type FieldType = 'text' | 'number' | 'date' | 'select' | 'textarea' | 'email' | 'tel';

export interface FormField {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  placeholder?: string;
  options?: { value: string | number; label: string }[];
  default?: string | number;
  hint?: string;
}

export interface FormConfig {
  title: string;
  fields: FormField[];
  submitLabel?: string;
  cancelLabel?: string;
  cancelPage?: string;
  onSubmit: (data: Record<string, any>) => Promise<{ ok: boolean; message?: string; redirectTo?: string }>;
}

export function renderForm(cfg: FormConfig): HTMLElement {
  const form = document.createElement('form');
  form.className = 'tn-form bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-3xl';
  form.setAttribute('autocomplete', 'off');

  // Fields
  for (const f of cfg.fields) {
    const wrap = document.createElement('div');
    wrap.className = 'mb-4';

    const label = document.createElement('label');
    label.className = 'block text-sm font-medium text-gray-700 mb-1';
    label.innerHTML = `${escapeHtml(f.label)}${f.required ? ' <span class="text-red-500">*</span>' : ''}`;
    wrap.appendChild(label);

    let input: HTMLElement;
    if (f.type === 'select') {
      const sel = document.createElement('select');
      sel.name = f.name;
      sel.className = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white';
      if (f.required) sel.required = true;
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = f.placeholder || `-- ${f.label} --`;
      sel.appendChild(placeholder);
      for (const opt of f.options || []) {
        const o = document.createElement('option');
        o.value = String(opt.value);
        o.textContent = opt.label;
        if (f.default !== undefined && String(f.default) === String(opt.value)) o.selected = true;
        sel.appendChild(o);
      }
      input = sel;
    } else if (f.type === 'textarea') {
      const ta = document.createElement('textarea');
      ta.name = f.name;
      ta.className = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
      ta.rows = 3;
      if (f.placeholder) ta.placeholder = f.placeholder;
      if (f.required) ta.required = true;
      if (f.default !== undefined) ta.value = String(f.default);
      input = ta;
    } else {
      const inp = document.createElement('input');
      inp.type = f.type;
      inp.name = f.name;
      inp.className = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
      inp.autocomplete = 'off';
      if (f.placeholder) inp.placeholder = f.placeholder;
      if (f.required) inp.required = true;
      if (f.default !== undefined) inp.value = String(f.default);
      input = inp;
    }
    wrap.appendChild(input);

    if (f.hint) {
      const hint = document.createElement('div');
      hint.className = 'text-xs text-gray-500 mt-1';
      hint.textContent = f.hint;
      wrap.appendChild(hint);
    }
    form.appendChild(wrap);
  }

  // Error display
  const errBox = document.createElement('div');
  errBox.className = 'hidden mb-3 p-3 bg-red-50 text-red-700 text-sm rounded-lg';
  form.appendChild(errBox);

  // Success display
  const okBox = document.createElement('div');
  okBox.className = 'hidden mb-3 p-3 bg-green-50 text-green-700 text-sm rounded-lg';
  form.appendChild(okBox);

  // Buttons
  const btnWrap = document.createElement('div');
  btnWrap.className = 'flex gap-3 pt-4 border-t border-gray-200';
  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'flex-1 px-4 py-2.5 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-shadow';
  submitBtn.style.background = 'linear-gradient(135deg, #3b82f6, #1e40af)';
  submitBtn.textContent = cfg.submitLabel || '💾 保存 / Lưu';
  btnWrap.appendChild(submitBtn);

  if (cfg.cancelPage) {
    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50';
    cancelBtn.textContent = cfg.cancelLabel || '取消 / Hủy';
    cancelBtn.addEventListener('click', () => { window.location.hash = `#${cfg.cancelPage}`; });
    btnWrap.appendChild(cancelBtn);
  }
  form.appendChild(btnWrap);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errBox.classList.add('hidden');
    okBox.classList.add('hidden');
    submitBtn.disabled = true;
    const origText = submitBtn.textContent;
    submitBtn.textContent = '提交中... / Đang gửi...';

    const data: Record<string, any> = {};
    for (const f of cfg.fields) {
      const el = form.querySelector(`[name="${f.name}"]`) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      data[f.name] = el ? el.value : '';
    }

    try {
      const result = await cfg.onSubmit(data);
      if (result.ok) {
        okBox.classList.remove('hidden');
        okBox.textContent = result.message || '✅ 保存成功 / Lưu thành công';
        if (result.redirectTo) {
          setTimeout(() => { window.location.hash = `#${result.redirectTo}`; }, 1000);
        } else {
          form.reset();
        }
      } else {
        errBox.classList.remove('hidden');
        errBox.textContent = result.message || '❌ 保存失败 / Lưu thất bại';
      }
    } catch (err) {
      errBox.classList.remove('hidden');
      errBox.textContent = '❌ ' + (err instanceof Error ? err.message : String(err));
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = origText;
    }
  });

  return form;
}

function escapeHtml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]!));
}
